import queryString from 'query-string';
import { Component, Mixins } from 'vue-property-decorator';
import PrivilegeMixin from './PrivilegeMixin';
import RoundButton from '@/components/RoundButton.vue';
import RoundButtonMenu from '@/components/RoundButtonMenu.vue';
import DetailField from '@/components/DetailField.vue';
import formatting from '@/formatting';
import { api } from '../api';
import { DetailLayoutITF, ReportFileTableITF } from '@/interfaces';
import { Getter, Mutation } from 'vuex-class';

@Component({
  components: { RoundButton, RoundButtonMenu, DetailField }
})
export default class CommonMixin extends Mixins(PrivilegeMixin) {
  public api = api;
  public queryString = queryString;
  public formatting = formatting;

  @Getter('getDeviceGroupID')
  public getDeviceGroupID!: () => number;

  @Mutation('setDeviceGroupID')
  public setDeviceGroupID;

  public get viewId() {
    return this.isSite() ? 2 : 3;
  }
  public get viewFilter() {
    return { devicegroupview_id: this.viewId };
  }
  public get serviceTypeFilter() {
    return this.$route.path.includes('serviceorders')
      ? { service_type: 0 }
      : { service_type: 1 };
  }
  public conclusionOptionList = [
    {
      value: false,
      text: 'NOK'
    },
    {
      value: true,
      text: 'OK'
    }
  ];
  public uploadTypeOptionList = [
    {
      value: 'report_file_id_nl',
      text: 'Rapport NL'
    },
    {
      value: 'report_file_id_fr',
      text: 'Rapport FR'
    },
    {
      value: 'certificate_file_id_nl',
      text: 'Certificaat NL'
    },
    {
      value: 'certificate_file_id_fr',
      text: 'Certificaat FR'
    }
  ];
  public async exportFields(apiUrl, id, fileType) {
    let fileTypeId = 0;
    if (fileType === 'excel') {
      // Excel
      fileTypeId = 1;
    } else {
      // JSON
      fileTypeId = 2;
    }
    const data = {
      filetype: fileTypeId
    };
    this.api.downloadAction(
      this.$store,
      apiUrl + String(id) + '/export_fields/',
      data
    );
  }
  public async save(rec, apiUrl, endpoint = 'file') {
    const ref: any = this.$refs.layout;
    if (ref.isNew) {
      const response = await this.api.createRecord(this.$store, apiUrl, rec);
      if (response) {
        ref.rec = response.data;
        await this.uploadFiles(ref.rec.id, apiUrl, endpoint);
        this.updateRecord(ref, ref.rec.id, apiUrl);
        this.$router.push(ref.url + '/' + ref.rec.id);
      }
    } else {
      const response = await this.api.updateRecord(
        this.$store,
        apiUrl + rec.id,
        rec
      );
      await this.uploadFiles(rec.id, apiUrl, endpoint);
      this.updateRecord(ref, rec.id, apiUrl);
      this.$router.push(ref.url + '/' + rec.id);
    }
  }
  public async saveWithFiles(apiUrl) {
    const ref: any = this.$refs.layout;
    if (ref.isNew) {
      const response = await this.api.createRecord(
        this.$store,
        apiUrl,
        ref.rec
      );
      if (response) {
        ref.rec = response.data;
        const filesRef: any = this.$refs.files;
        await (filesRef as ReportFileTableITF).saveAllFiles(ref.rec.id);
        this.$router.push(ref.url + '/' + ref.rec.id);
      }
    } else {
      (ref as DetailLayoutITF).commitRecord(ref.rec);
    }
  }
  public async updateRecord(ref, id, apiUrl) {
    const recordResponse = await this.api.getRecord(this.$store, apiUrl + id);
    if (recordResponse) {
      ref.rec = recordResponse.data;
      ref.theForm.resetValidation();
    }
    ref.loading = false;
  }
  public async uploadFiles(id, apiUrl, endpoint) {
    const ref: any = this.$refs.upload;
    // Only execute when at least 1 file is selected
    if (ref.files.length > 0) {
      ref.errorMessages = '';
      const formData = new FormData();
      for (const x of Object.keys(ref.files)) {
        const file: Blob = ref.files[x];
        formData.append('file', file);
      }
      const response = await this.api.uploadFile(
        this.$store,
        apiUrl + id + '/' + endpoint,
        formData
      );
      ref.files = [];
    }
  }
  public async uploadExternalReport(apiUrl, id, upload) {
    const ref: any = this.$refs.upload;
    // Only execute when at least 1 file is selected
    if (ref.files.length > 0) {
      ref.errorMessages = '';
      const formData = new FormData();
      for (const x of Object.keys(ref.files)) {
        const file: Blob = ref.files[x];
        formData.append('file', file);
      }
      const response = await this.api.uploadFile(
        this.$store,
        apiUrl + id + '/' + upload.type + '/upload',
        formData
      );
      ref.files = [];
    }
  }
  public buildOptionList(list) {
    return this.formatting.formatList(list);
  }
  public getDataType(type) {
    return this.formatting.formatDataType(type);
  }
  public getField(type) {
    if (this.getDataType(type) === 'optionlist') {
      return 'value_index';
    } else {
      return 'value';
    }
  }
  public openGoogleMapsRoute(coordSrc, coordDst) {
    if (coordSrc && coordDst) {
      let link = 'https://www.google.com/maps/dir/?api=1';
      link += '&origin=' + coordSrc.lat + ',' + coordSrc.lon;
      link += '&destination=' + coordDst.lat + ',' + coordDst.lon;
      window.open(link, '_blank');
    }
  }
  public duplicateRecord = async function duplicateRecord(
    store,
    router,
    apiUrl,
    newProps,
    clientURL
  ) {
    const response = await api.duplicateRecord(store, apiUrl, newProps);
    if (response) {
      router.push(clientURL + '/' + response.data.id);
    }
  };
  public openDeviceReport(id) {
    if (id) {
      const routeData = this.$router.resolve({
        path: '/devices/measurereports/' + String(id)
      });
      window.open(routeData.href, '_blank');
    }
  }
  public openDeviceServiceReport(id) {
    if (id) {
      const routeData = this.$router.resolve({
        path: '/devices/servicereports/' + String(id)
      });
      window.open(routeData.href, '_blank');
    }
  }
  public openSite(id) {
    if (id) {
      const path = this.isSite()
        ? '/sites/sites/' + String(id)
        : '/routecontrols/routecontrols/' + String(id);
      const routeData = this.$router.resolve({
        path
      });
      window.open(routeData.href, '_blank');
    }
  }
  public openNewServiceOrder(queryObj) {
    if (queryObj) {
      const path = this.isSite()
        ? '/sites/serviceorders/new?' + queryString.stringify(queryObj)
        : '/routecontrols/serviceorders/new?' + queryString.stringify(queryObj);
      const routeData = this.$router.resolve({
        path
      });
      window.open(routeData.href, '_blank');
    }
  }
  public openNewNotification(queryObj) {
    if (queryObj) {
      const path = this.isSite()
        ? '/sites/notifications/new?' + queryString.stringify(queryObj)
        : '/routecontrols/notifications/new?' + queryString.stringify(queryObj);
      const routeData = this.$router.resolve({
        path
      });
      window.open(routeData.href, '_blank');
    }
  }
  public openEM(id) {
    if (id) {
      window.open(
        'https://apps.mow.vlaanderen.be/eminfra/installaties/' + id,
        '_blank'
      );
    }
  }
  public openSiteReport(id) {
    if (id) {
      const path = this.isSite()
        ? '/sites/reports/' + String(id)
        : '/routecontrols/reports/' + String(id);
      const routeData = this.$router.resolve({
        path
      });
      window.open(routeData.href, '_blank');
    }
  }
  public otpStatusText = function otpStatusText(value) {
    if (value === 'Off') {
      return 'Uit';
    } else if (value === 'WaitForSetup') {
      return 'Klaar voor instellen';
    } else if (value === 'On') {
      return 'Aan';
    } else if (value === 'Expired') {
      return 'Verlopen';
    } else {
      return '';
    }
  };
  public otpStatusColor = function otpStatusColor(value) {
    if (value === 'Off') {
      return 'grey lighten-2';
    } else if (value === 'WaitForSetup') {
      return 'primary';
    } else if (value === 'On') {
      return 'success';
    } else if (value === 'Expired') {
      return 'error';
    } else {
      return 'grey lighten-2';
    }
  };
  public getTableCfg(tableCfg, key, context = this) {
    if (typeof tableCfg[key] === 'function') {
      return tableCfg[key](context);
    } else {
      return tableCfg[key];
    }
  }

  public isCamera() {
    return this.getDeviceGroupID() === 1;
  }
  public isBreathAnalysis() {
    return this.getDeviceGroupID() === 4;
  }
  public isCurvo() {
    return this.getDeviceGroupID() === 2;
  }
  public isTaxa() {
    return this.getDeviceGroupID() === 5;
  }
  public isSite() {
    return this.$route.path.includes('routecontrols') ? false : true;
  }
  public isServiceOrder() {
    return this.$route.path.includes('serviceorders') ? true : false;
  }
  public getSiteTitle() {
    return this.isSite() ? 'Site' : 'Traject Controle';
  }
  public getViewTitle() {
    return this.isServiceOrder() ? 'Dienstbevel' : 'Melding';
  }
}
